import { useState, useEffect, useContext } from 'react';
import { ref, get, set, update } from "firebase/database";
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthContext } from '../AuthContext';
import { motion } from 'framer-motion';
import './Login.css';

const AuthForm = () => {
  const { login } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [address, setAddress] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [forgotPhone, setForgotPhone] = useState('');
  const [otpEntered, setOtpEntered] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [adminData, setAdminData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Fetch admin credentials
    const adminRef = ref(db, 'Watch/admin');
    get(adminRef)
      .then((snapshot) => {
        if (snapshot.exists()) {
          setAdminData(snapshot.val());
        }
      })
      .catch((err) => {
        console.error('Error loading admin credentials:', err);
        toast.error('Failed to load admin configuration');
      });

    // Fetch customers data
    const customersRef = ref(db, 'Watch/customers');
    get(customersRef)
      .then((snapshot) => {
        if (snapshot.exists()) {
          const customersData = Object.entries(snapshot.val()).map(([id, data]) => ({
            id,
            ...data
          }));
          setCustomers(customersData);
        }
      })
      .catch((err) => console.error('Error loading customers:', err));
  }, []);

  const handleToggleForm = () => {
    setIsLogin(!isLogin);
    setErrors({});
    resetForm();
  };

  const sendOtp = () => {
    if (!forgotPhone) {
      toast.error('Please enter your phone number.');
      return;
    }

    if (forgotPhone.length !== 10 || isNaN(forgotPhone)) {
      toast.error('Please enter a valid 10-digit phone number.');
      return;
    }

    const customer = customers.find(customer => customer.mobile === forgotPhone);
    if (!customer) {
      toast.error('Mobile number not found in our records.');
      return;
    }

    const generatedOtp = Math.floor(100000 + Math.random() * 900000);
    setOtp(generatedOtp.toString());

    // In a real app, you would send this OTP via SMS service
    toast.success(`OTP sent to ${forgotPhone} (Demo: ${generatedOtp})`);
    setOtpSent(true);
  };

  const verifyOtp = () => {
    if (otpEntered === otp) {
      toast.success('OTP Verified! You can reset your password.');
      setOtpVerified(true);
    } else {
      toast.error('Invalid OTP. Try again.');
    }
  };

  const resetPassword = async () => {
    if (!newPassword || !confirmNewPassword) {
      toast.error('Please enter and confirm your new password.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    const customer = customers.find(customer => customer.mobile === forgotPhone);
    if (customer) {
      try {
        const userRef = ref(db, `Watch/customers/${customer.id}`);
        await update(userRef, { password: newPassword });
        toast.success('Password reset successfully!');
        setShowForgotPassword(false);
        resetForm();
      } catch (err) {
        toast.error('Error resetting password. Try again.');
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!mobile) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(mobile)) {
      newErrors.mobile = 'Mobile number should be exactly 10 digits';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password should be at least 6 characters';
    }

    if (!isLogin) {
      if (!username) {
        newErrors.username = 'Name is required';
      } else if (!/^[a-zA-Z\s]+$/.test(username)) {
        newErrors.username = 'Name should only contain letters and spaces';
      }

      if (!address) {
        newErrors.address = 'Address is required';
      }

      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      if (isLogin) {
        // Admin login check
        if (adminData && mobile === adminData.mobile && password === adminData.password) {
          const adminSession = {
            isAdmin: true,
            username: 'Admin',
            mobile: adminData.mobile,
            id: 'admin',
          };

          localStorage.setItem('authToken', 'admin-token');
          localStorage.setItem('userData', JSON.stringify(adminSession));
          login(adminSession);
          toast.success('Admin login successful!');
          setTimeout(() => navigate('/AdminDashboard'), 1000);
          return;
        }

        // Customer login check
        const customer = customers.find(
          c => c.mobile === mobile && c.password === password
        );

        if (customer) {
          const customerSession = {
            username: customer.username,
            mobile: customer.mobile,
            id: customer.id,
            isAdmin: false
          };

          localStorage.setItem('authToken', 'customer-token');
          localStorage.setItem('userData', JSON.stringify(customerSession));
          login(customerSession);
          toast.success('Login successful!');
          setTimeout(() => navigate('/'), 1000);
        } else {
          toast.error('Invalid mobile number or password');
        }
      } else {
        // Registration logic
        const customerExists = customers.some(
          customer => customer.mobile === mobile
        );
        
        if (customerExists) {
          toast.error('Mobile number already registered. Please login instead.');
          return;
        }

        const customerData = {
          username,
          address,
          password,
          mobile,
        };
      
        const userRef = ref(db, `Watch/customers/${mobile}`);
        await set(userRef, customerData);
        
        // Automatically log in the new customer
        const customerSession = { 
          username,
          role: 'customer',
          id: mobile,
          mobile,
          isAdmin: false
        };
        localStorage.setItem('authToken', 'customer-token');
        localStorage.setItem('userData', JSON.stringify(customerSession));
        login(customerSession);
        
        toast.success('Registration successful! You are now logged in.');
        setTimeout(() => navigate('/'), 1000);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setUsername('');
    setAddress('');
    setMobile('');
    setPassword('');
    setConfirmPassword('');
    setForgotPhone('');
    setOtpEntered('');
    setNewPassword('');
    setConfirmNewPassword('');
    setOtpVerified(false);
    setOtpSent(false);
    setErrors({});
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobile(value);
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s]*$/.test(value)) {
      setUsername(value);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      </div>

      <motion.div 
        className="auth-card"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        key={isLogin ? 'login' : 'register'}
      >
        <motion.div className="auth-header" variants={itemVariants}>
          <div className="auth-logo">
            <div className="logo-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="url(#gradient)"/>
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#667eea" />
                    <stop offset="100%" stopColor="#764ba2" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h1>WatchStore</h1>
          </div>
          <motion.p className="auth-subtitle" variants={itemVariants}>
            सुरक्षिततेच्या उद्देशाने मोबाईल नंबर वापरून लॉगिन करा
          </motion.p>
        </motion.div>

        <motion.div className="auth-toggle" variants={itemVariants}>
          <div className="toggle-container">
            <button 
              className={`toggle-btn ${isLogin ? 'active' : ''}`}
              onClick={() => setIsLogin(true)}
              type="button"
            >
              Login
            </button>
            <button 
              className={`toggle-btn ${!isLogin ? 'active' : ''}`}
              onClick={() => setIsLogin(false)}
              type="button"
            >
              Register
            </button>
            <div className={`toggle-slider ${isLogin ? 'left' : 'right'}`}></div>
          </div>
        </motion.div>

        <motion.form onSubmit={handleSubmit} className="auth-form" variants={itemVariants}>
          {!isLogin && (
            <>
              <motion.div className="input-group" variants={itemVariants}>
                <label htmlFor="username" className="input-label">Full Name</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="username"
                    className={`auth-input ${errors.username ? 'error' : ''}`}
                    placeholder="Enter your full name"
                    value={username}
                    onChange={handleNameChange}
                  />
                  <div className="input-icon">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                </div>
                {errors.username && <span className="error-message">{errors.username}</span>}
              </motion.div>

              <motion.div className="input-group" variants={itemVariants}>
                <label htmlFor="address" className="input-label">Address</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="address"
                    className={`auth-input ${errors.address ? 'error' : ''}`}
                    placeholder="Enter your address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                  <div className="input-icon">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                </div>
                {errors.address && <span className="error-message">{errors.address}</span>}
              </motion.div>
            </>
          )}

          <motion.div className="input-group" variants={itemVariants}>
            <label htmlFor="mobile" className="input-label">Mobile Number</label>
            <div className="input-wrapper">
              <input
                type="tel"
                id="mobile"
                className={`auth-input ${errors.mobile ? 'error' : ''}`}
                placeholder="Enter 10-digit mobile number"
                value={mobile}
                onChange={handleMobileChange}
                maxLength="10"
              />
              <div className="input-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
            </div>
            {errors.mobile && <span className="error-message">{errors.mobile}</span>}
          </motion.div>

          <motion.div className="input-group" variants={itemVariants}>
            <label htmlFor="password" className="input-label">Password</label>
            <div className="input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className={`auth-input ${errors.password ? 'error' : ''}`}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L12 12m6.121-3.878a3 3 0 00-4.243-4.243m4.243 4.243L21 21m-4.878-6.878L12 12m-3.878-3.878L9.878 9.878z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </motion.div>

          {!isLogin && (
            <motion.div className="input-group" variants={itemVariants}>
              <label htmlFor="confirmPassword" className="input-label">Confirm Password</label>
              <div className="input-wrapper">
                <input
                  type="password"
                  id="confirmPassword"
                  className={`auth-input ${errors.confirmPassword ? 'error' : ''}`}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <div className="input-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </motion.div>
          )}

          <motion.button 
            type="submit" 
            className={`auth-submit ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
            variants={itemVariants}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : (
              isLogin ? 'Login' : 'Create Account'
            )}
          </motion.button>

          {isLogin && (
            <motion.button 
              type="button" 
              className="forgot-password-btn"
              onClick={() => setShowForgotPassword(!showForgotPassword)}
              variants={itemVariants}
            >
              Forgot Password?
            </motion.button>
          )}
        </motion.form>

        {showForgotPassword && isLogin && (
          <motion.div 
            className="forgot-password-section"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h3>Reset Password</h3>
            
            {!otpSent ? (
              <>
                <div className="input-group">
                  <label htmlFor="forgotPhone" className="input-label">Mobile Number</label>
                  <div className="input-wrapper">
                    <input
                      type="tel"
                      id="forgotPhone"
                      className="auth-input"
                      placeholder="Enter registered mobile number"
                      value={forgotPhone}
                      onChange={(e) => setForgotPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      maxLength="10"
                    />
                  </div>
                </div>
                <button type="button" onClick={sendOtp} className="auth-submit">
                  Send OTP
                </button>
              </>
            ) : !otpVerified ? (
              <>
                <div className="input-group">
                  <label htmlFor="otp" className="input-label">Enter OTP</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="otp"
                      className="auth-input"
                      placeholder="Enter 6-digit OTP"
                      value={otpEntered}
                      onChange={(e) => setOtpEntered(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength="6"
                    />
                  </div>
                </div>
                <button type="button" onClick={verifyOtp} className="auth-submit">
                  Verify OTP
                </button>
              </>
            ) : (
              <>
                <div className="input-group">
                  <label htmlFor="newPassword" className="input-label">New Password</label>
                  <div className="input-wrapper">
                    <input
                      type="password"
                      id="newPassword"
                      className="auth-input"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>
                </div>
                <div className="input-group">
                  <label htmlFor="confirmNewPassword" className="input-label">Confirm New Password</label>
                  <div className="input-wrapper">
                    <input
                      type="password"
                      id="confirmNewPassword"
                      className="auth-input"
                      placeholder="Confirm new password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                    />
                  </div>
                </div>
                <button type="button" onClick={resetPassword} className="auth-submit">
                  Reset Password
                </button>
              </>
            )}
          </motion.div>
        )}

        <motion.div className="auth-footer" variants={itemVariants}>
          <p>
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button 
              type="button" 
              className="auth-link"
              onClick={handleToggleForm}
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </motion.div>
      </motion.div>

      <ToastContainer 
        position="top-right" 
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
};

export default AuthForm;
